import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { HostPublicProfile } from '@/mocks/hostProfile';
import { getSocialStats } from '@/mocks/rfwSocial';
import ShareModal from '@/components/feature/ShareModal';
import LikeButton from '@/components/feature/LikeButton';
import ViralStats from '@/components/feature/ViralStats';

interface Props {
  profile: HostPublicProfile;
}

export default function HostCoverHeader({ profile }: Props) {
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(profile.followers);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showNotifToast, setShowNotifToast] = useState(false);

  const socialStats = getSocialStats('host', profile.id);

  const handleFollow = () => {
    if (isFollowing) {
      setIsFollowing(false);
      setFollowersCount((p) => p - 1);
    } else {
      setIsFollowing(true);
      setFollowersCount((p) => p + 1);
    }
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const handleNotif = () => {
    setShowNotifToast(true);
    setTimeout(() => setShowNotifToast(false), 2000);
  };

  const statusLabel = profile.status === 'on_air' ? 'AL AIRE' : profile.status === 'active' ? 'Activo' : 'Inactivo';
  const statusDot = profile.status === 'on_air' ? 'bg-red-500' : profile.status === 'active' ? 'bg-emerald-500' : 'bg-foreground-400';

  return (
    <>
      <div className="relative w-full">
        {/* Cover */}
        <div className="relative w-full h-48 md:h-64 overflow-hidden">
          <img
            src={profile.coverUrl}
            alt={`Portada de ${profile.name}`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/50"></div>
        </div>

        {/* Profile info + actions */}
        <div className="max-w-5xl mx-auto px-4 md:px-6 relative">
          <div className="flex flex-col md:flex-row md:items-end gap-4 -mt-16 md:-mt-20 relative z-10">
            {/* Avatar */}
            <div className="relative">
              <div className="w-28 h-28 md:w-36 md:h-36 rounded-full border-4 border-background-50 overflow-hidden bg-background-50 flex-shrink-0">
                <img
                  src={profile.photoUrl}
                  alt={profile.name}
                  className="w-full h-full object-cover"
                />
              </div>
              {profile.status === 'on_air' && (
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1 cursor-default">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                  EN VIVO
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 pb-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">{profile.name}</h1>
                {profile.verified && (
                  <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-500 text-background-50" title="Verificado">
                    <i className="ri-verified-badge-fill text-sm"></i>
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <p className="text-foreground-600 text-sm">{profile.username}</p>
                <span className="text-foreground-300">·</span>
                <Link
                  to={`/radio/${profile.radioSlug}`}
                  className="text-sm text-accent-600 hover:text-accent-700 font-medium whitespace-nowrap cursor-pointer"
                >
                  {profile.radioName} {profile.radioFrequency}
                </Link>
                <span className="text-foreground-300">·</span>
                <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${profile.status === 'on_air' ? 'bg-red-100 text-red-700' : profile.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-foreground-100 text-foreground-500'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${statusDot}`}></span>
                  {statusLabel}
                </span>
              </div>
              <p className="text-foreground-700 text-sm mt-2 max-w-lg leading-relaxed">{profile.bio}</p>

              {/* Stats row */}
              <div className="flex items-center gap-5 mt-3 flex-wrap">
                <div className="text-center md:text-left">
                  <span className="text-foreground-950 font-bold text-lg leading-none">{followersCount.toLocaleString()}</span>
                  <span className="text-foreground-500 text-xs block">Seguidores</span>
                </div>
                <div className="text-center md:text-left">
                  <span className="text-foreground-950 font-bold text-lg leading-none">{profile.programs.length}</span>
                  <span className="text-foreground-500 text-xs block">Programas</span>
                </div>
                <div className="text-center md:text-left">
                  <span className="text-foreground-950 font-bold text-lg leading-none">{profile.stats.totalPosts}</span>
                  <span className="text-foreground-500 text-xs block">Publicaciones</span>
                </div>
                <div className="text-center md:text-left">
                  <span className="text-foreground-950 font-bold text-lg leading-none">{profile.stats.totalVideos}</span>
                  <span className="text-foreground-500 text-xs block">Videos</span>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2 flex-shrink-0 md:pb-1">
              <button
                onClick={handleFollow}
                className={`px-5 py-2.5 rounded-xl font-semibold text-sm whitespace-nowrap cursor-pointer transition-all ${
                  isFollowing
                    ? 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                    : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                }`}
              >
                {isFollowing ? (
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-follow-line"></i> Siguiendo
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-add-line"></i> Seguir
                  </span>
                )}
              </button>

              {/* Me Gusta */}
              <LikeButton
                entityId={profile.id}
                entityType="host"
                initialLikes={socialStats?.likes || 0}
                size="md"
              />

              {/* Share */}
              <button
                onClick={handleShare}
                className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-100 hover:bg-background-200 text-foreground-600 transition-all cursor-pointer"
              >
                <i className="ri-share-forward-line text-lg"></i>
              </button>

              <div className="relative">
                <button
                  onClick={handleNotif}
                  className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-100 hover:bg-background-200 text-foreground-600 transition-all cursor-pointer"
                >
                  <i className="ri-notification-3-line text-lg"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {showNotifToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-foreground-900 text-background-50 px-5 py-3 rounded-xl text-sm font-medium z-50 shadow-lg flex items-center gap-2 animate-bounce">
          <i className="ri-notification-3-fill text-accent-400"></i>
          Notificaciones activadas
        </div>
      )}

      {/* Close share menu on outside click */}
      {showShareModal && (
        <div className="fixed inset-0 z-40" onClick={() => setShowShareModal(false)}></div>
      )}

      {/* Share Modal */}
      <ShareModal
        entityName={profile.name}
        entitySubtitle={`@${profile.username}`}
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
    </>
  );
}